/* ══════════════════════════════════════════════════════════════
   Modulo — shared header pin engine
   Used by BOTH `section-header-pill` and `section-header-commerce`;
   edit it with both siblings in mind.

   A sticky header is two components in series and they fail differently:
   the section's own JS toggles a class, and CSS pins the bar with
   `position: fixed`. This file owns everything that makes the SECOND half
   survive a real merchant theme, because that is where every reported
   "the header is still not sticky" has actually lived:

     - `position: fixed` only resolves against the viewport while no ancestor
       is a containing block for fixed descendants, and a theme owns two of
       the three wrappers our headers sit inside
     - the offender can arrive AFTER the first stick, so the check cannot be
       one-shot, and the repair cannot be an inline style a theme can rewrite
     - a theme that scrolls an inner container leaves pageYOffset at 0, so the
       class is never applied at all and there is nothing to repair
     - the bar can be laid out perfectly and painted nowhere, clipped by an
       ancestor's overflow or buried under a higher stacking order

   Every branch is EMPIRICAL: it measures the failure and stops the moment the
   measurement comes good, so the blast radius stays one property on one
   ancestor rather than a blanket reset of the theme's boxes.

   Pinned by scripts/probe-header-pill-sticky.mjs (112 checks; 46 fail with the
   repair disabled via --control).
   ══════════════════════════════════════════════════════════════ */
(function () {
  if (window.moduloHeaderPin) return;
  var PIN_SEQ = 0;

  /**
   * @param root      the section's own root element
   * @param opts.head the element carrying `position: fixed`
   * @param opts.bar  the visible bar, used for the paint checks
   * @param opts.wrap selector for the wrapper that owns our stacking context
   * @param opts.cleanup array to push undo functions onto (optional)
   */
  window.moduloHeaderPin = function (root, opts) {
    opts = opts || {};
    var head = opts.head || root;
    var bar = opts.bar || head;
    var wrapSel = opts.wrap || '.shopify-section';
    var cleanup = opts.cleanup || [];

    /* ── transform-proof fixed ───────────────────────────────
       `position: fixed` resolves against the viewport ONLY while no
       ancestor is a containing block for fixed descendants. transform,
       perspective, filter, backdrop-filter, will-change and contain each
       make one — and this header sits inside two wrappers we do not
       author (`.shopify-section`, `#shopify-section-group-header-group`)
       plus <body>. A theme that sets any of those on either wrapper turns
       the stuck bar into an absolutely-positioned box that scrolls away
       with the page: the class is on, our CSS is correct, and the header
       simply is not sticky, with nothing in the settings to explain it.
       Measured across both headers; see the probe for the full matrix
       (scripts/probe-header-pill-sticky.mjs).

       Detection is EMPIRICAL rather than a property allowlist: a probe at
       `position:fixed; top:0; bottom:0` reports its containing block's box,
       so comparing that to the viewport catches whatever caused it — a
       property this list has not heard of included. Then neutralise the
       nearest offender, re-probe, and stop the moment the pin works, so
       the blast radius is one property on one ancestor rather than a
       blanket reset of the theme's wrappers. Restored on unload. */
    var NEUTRAL = {
      'transform': 'none',
      'perspective': 'none',
      'filter': 'none',
      'backdrop-filter': 'none',
      '-webkit-backdrop-filter': 'none',
      'will-change': 'auto',
      'contain': 'none',
      'content-visibility': 'visible',
      'transform-style': 'flat',
      'offset-path': 'none',
      'translate': 'none',
      'rotate': 'none',
      'scale': 'none',
      'animation-name': 'none',
      'overflow': 'visible'
    };

    function fixedIsBroken() {
      var probe = document.createElement('div');
      // clientHeight, not innerHeight: it already excludes a horizontal
      // scrollbar, which is exactly what the probe's own box excludes.
      probe.style.cssText = 'position:fixed;top:0;bottom:0;left:0;width:1px;visibility:hidden;pointer-events:none;';
      root.appendChild(probe);
      var r = probe.getBoundingClientRect();
      probe.parentNode.removeChild(probe);
      return Math.abs(r.top) > 1 ||
             Math.abs(r.height - document.documentElement.clientHeight) > 1;
    }

    function offendersOn(node) {
      var cs = window.getComputedStyle(node);
      var bad = [];
      if (cs.transform && cs.transform !== 'none') bad.push('transform');
      if (cs.perspective && cs.perspective !== 'none') bad.push('perspective');
      if (cs.filter && cs.filter !== 'none') bad.push('filter');
      if (cs.backdropFilter && cs.backdropFilter !== 'none') bad.push('backdrop-filter');
      if (cs.webkitBackdropFilter && cs.webkitBackdropFilter !== 'none') bad.push('-webkit-backdrop-filter');
      if (cs.willChange && /transform|translate|rotate|scale|perspective|filter|contain/.test(cs.willChange)) bad.push('will-change');
      if (cs.contain && /paint|layout|strict|content/.test(cs.contain)) bad.push('contain');
      // The INDEPENDENT transform properties are separate computed values, so
      // reading `transform` alone cannot see them: measured, each of
      // `translate`, `rotate` and `scale` creates a containing block on its own
      // while `getComputedStyle(node).transform` still reads "none".
      if (cs.translate && cs.translate !== 'none') bad.push('translate');
      if (cs.rotate && cs.rotate !== 'none') bad.push('rotate');
      if (cs.scale && cs.scale !== 'none') bad.push('scale');
      // A RUNNING transform animation keeps the containing block alive in
      // Chromium even after `transform: none !important` wins the cascade
      // (animations outrank author !important), so the animation itself has to
      // stop. Gated on the node actually carrying a transform right now, so an
      // unrelated fade is never touched — AND gated on the animation being
      // INFINITE, which is the load-bearing half.
      //
      // Cancelling a finite animation is vandalism, not a repair. Measured: on
      // a theme whose page-in transition is `lock scroll -> animate -> unlock
      // on animationend` — the commonest shape there is, and one that only
      // runs ON NAVIGATION — this rule deleted the animation outright (the
      // theme's `getAnimations()` went from ["themePageIn"] to []), so
      // `animationend` could never fire, the theme's own unlock never ran, and
      // the page stayed unscrollable until a reload. A cancelled animation
      // fires `animationcancel`, which nothing listens for.
      //
      // A finite animation is a TRANSIENT offender: it ends on its own and the
      // containing block goes with it, so the honest repair is to wait (see
      // transientAncestor). An infinite one never ends, so nothing can be
      // waiting on it and stopping it is safe.
      if (cs.animationName && cs.animationName !== 'none' &&
          /infinite/.test(cs.animationIterationCount || '') &&
          ((cs.transform && cs.transform !== 'none') || (cs.translate && cs.translate !== 'none') ||
           (cs.rotate && cs.rotate !== 'none') || (cs.scale && cs.scale !== 'none'))) bad.push('animation-name');
      // content-visibility applies containment WITHOUT changing the computed
      // `contain` property, so the test above cannot see it: a theme that sets
      // `content-visibility: auto` on a wrapper (a common perf tweak) creates a
      // containing block while `getComputedStyle(node).contain` still reads
      // "none". Measured: the walk went straight past it and the bar scrolled
      // away with the page.
      if (cs.contentVisibility && cs.contentVisibility !== 'visible') bad.push('content-visibility');
      // Both measured breaking the pin with computed `transform` still "none",
      // so reading `transform` alone can never see either of them.
      if (cs.transformStyle && cs.transformStyle !== 'flat') bad.push('transform-style');
      if (cs.offsetPath && cs.offsetPath !== 'none') bad.push('offset-path');
      return bad;
    }

    /* Is something in the chain mid-animation right now? A finite animation or
       a transition on a containing-block property breaks the pin for as long as
       it runs and then fixes itself. Repairing it would be both unnecessary and
       destructive, so the pin simply waits: the bar is unpinned for the few
       hundred milliseconds a page-in transition lasts, which nobody can see. */
    function transientAncestor() {
      var node = root;
      while (node && node.nodeType === 1) {
        if (node.getAnimations) {
          var live = node.getAnimations();
          for (var i = 0; i < live.length; i++) {
            var a = live[i];
            if (a.playState !== 'running') continue;
            // An infinite animation never ends, so waiting on it is waiting
            // forever — that one is a real offender and is repaired above.
            var it = a.effect && a.effect.getTiming ? a.effect.getTiming().iterations : 1;
            if (it !== Infinity) return true;
          }
        } else {
          var cs = window.getComputedStyle(node);
          if (cs.animationName && cs.animationName !== 'none' &&
              !/infinite/.test(cs.animationIterationCount || '')) return true;
        }
        if (node === document.documentElement) break;
        node = node.parentNode;
      }
      return false;
    }

    /* Neutralise from a stylesheet !important rule rather than an inline style.
       Cascade order puts an IMPORTANT author declaration above a NORMAL inline
       one, so this also survives a theme whose own scroll JS keeps rewriting
       `element.style.transform` on the wrapper — an inline repair loses that
       fight every frame, and the merchant sees a header that sticks for one
       frame and then scrolls away. */
    var pinSheet = null;
    function neutralise(node, props) {
      if (!pinSheet) {
        pinSheet = document.createElement('style');
        (document.head || document.documentElement).appendChild(pinSheet);
        cleanup.push(function () {
          if (pinSheet && pinSheet.parentNode) pinSheet.parentNode.removeChild(pinSheet);
          pinSheet = null;
        });
      }
      var body = [];
      for (var i = 0; i < props.length; i++) body.push(props[i] + ':' + NEUTRAL[props[i]] + ' !important');
      return forceDecls(node, body.join(';'));
    }

    function forceDecls(node, decls) {
      if (!pinSheet) {
        pinSheet = document.createElement('style');
        (document.head || document.documentElement).appendChild(pinSheet);
        cleanup.push(function () {
          if (pinSheet && pinSheet.parentNode) pinSheet.parentNode.removeChild(pinSheet);
          pinSheet = null;
        });
      }
      var cls = 'modulo-pin-fix-' + (++PIN_SEQ);
      node.classList.add(cls);
      var undo = function () { node.classList.remove(cls); };
      cleanup.push(undo);
      pinSheet.appendChild(document.createTextNode('.' + cls + '{' + decls + '}'));
      return undo;
    }

    var pinChecked = false, pinRepairs = 0;
    function ensurePinWorks() {
      if (pinChecked) return;
      if (!fixedIsBroken()) { pinChecked = true; return; }
      /* Leave pinChecked false so the next scroll tick looks again — by then
         the animation has ended and there is usually nothing left to repair.
         This also costs no repair budget, because onStick only spends one when
         a check has actually been made. */
      if (transientAncestor()) return;
      pinChecked = true;

      var node = root;
      while (node && node.nodeType === 1) {
        var bad = offendersOn(node);
        if (bad.length) {
          neutralise(node, bad);
          if (!fixedIsBroken()) return;
        }
        if (node === document.documentElement) break;
        node = node.parentNode;
      }

      /* The walk above is still an allowlist, and this list has now been wrong
         twice in production. If the pin is broken and nothing recognised it, try
         the WHOLE neutral set on each ancestor and keep only what measurably
         helps, so a property nobody here has heard of gets repaired rather than
         shrugged at. Rolled back per node when it does not help, which keeps the
         blast radius identical to the targeted path. */
      if (!fixedIsBroken()) return;
      var all = [];
      for (var k in NEUTRAL) {
        // `overflow` cannot create a containing block for a fixed descendant,
        // so it can never be the reason the pin is broken. It is in the map for
        // the clipping repair below, and forcing it here would only strip a
        // theme's scrollport or its `overflow-x: hidden` mobile guard for
        // nothing.
        if (k !== 'overflow' && Object.prototype.hasOwnProperty.call(NEUTRAL, k)) all.push(k);
      }
      node = root;
      while (node && node.nodeType === 1) {
        var undo = neutralise(node, all);
        if (!fixedIsBroken()) return;
        undo();
        if (node === document.documentElement) break;
        node = node.parentNode;
      }
    }

    /* A box that actually scrolls. Shared by findScroller (which wants it) and
       by the clipping repair (which must never touch it): a box that scrolls is
       not what is hiding the bar — a fixed or absolute bar escapes an
       `overflow: hidden` CLIP, it does not escape a scrollport it is not in —
       and forcing `overflow: visible` on it destroys the page's scrolling. */
    function isScroller(node) {
      var cs = window.getComputedStyle(node);
      return /(auto|scroll)/.test(cs.overflowY) && node.scrollHeight > node.clientHeight + 1;
    }

    /* The box the PAGE scrolls in, which is the only one the clipping repair
       must keep its hands off. `isScroller` alone is too generous here: an
       element carrying the near-universal `overflow-x: hidden` mobile guard
       computes `overflow-y: auto` (one axis cannot stay `visible` while the
       other is not), and our own overlay head overflowing it is enough to make
       scrollHeight exceed clientHeight — so the section wrapper that is
       CLIPPING the bar reads as a scroller and never gets repaired. Measured:
       that alone cost the overlay/overflow-x-hidden case in the sibling probe.
       A real page scrollport is viewport-tall; a clipped header wrapper is not. */
    function isPageScroller(node) {
      return isScroller(node) &&
             node.clientHeight >= document.documentElement.clientHeight * 0.8;
    }

    /* ── overlay: the bar has to be VISIBLE over the hero ─────────
       In overlay mode the head is `position: absolute` inside a wrapper that
       collapses to ZERO height, so a theme's `overflow: hidden` on
       `.shopify-section` or on the header group clips it out of existence.
       The moment it goes fixed it escapes that clip, so what the merchant sees
       is a header that is missing on the hero and then appears once they
       scroll — which reads as "it is not sitting on my hero image".
       Measured: bar present, correctly placed, painting nothing.

       Bounded on purpose — the walk stops at the header group and never
       reaches <body>, whose `overflow-x: hidden` is a deliberate mobile guard
       we must not remove. */
    function barHidden() {
      if (!bar) return false;
      var r = bar.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      var x = Math.round(r.left + r.width / 2);
      var y = Math.round(r.top + r.height / 2);
      if (x < 0 || y < 0 || y > document.documentElement.clientHeight) return false;
      var hit = document.elementFromPoint(x, y);
      return !!hit && !root.contains(hit);
    }

    /* Deliberately NOT gated on overlay mode: a pinned solid header is buried
       by a high z-index exactly the same way, and every branch here is already
       gated on the bar being PROVABLY invisible, so it costs nothing when the
       header is fine. */
    var visTries = 0;
    function ensureBarVisible() {
      if (visTries >= 3 || !barHidden()) return;
      visTries++;

      // 1. a clipping ancestor (overlay's zero-height containing block).
      var node = root.parentNode;
      while (node && node.nodeType === 1 && node !== document.body) {
        var cs = window.getComputedStyle(node);
        if ((cs.overflowX !== 'visible' || cs.overflowY !== 'visible') && !isPageScroller(node)) {
          /* Roll back what does not help. This walk used to keep every rule it
             wrote even when the bar stayed invisible, so it could strip several
             theme boxes and fix nothing. */
          var undo = neutralise(node, ['overflow']);
          if (!barHidden()) return;
          undo();
        }
        /* Stop at the header GROUP. Shopify derives this id from the section
           group's FILENAME, while our `enabled_on: {groups:["header"]}` matches
           its TYPE — so `sections/header.json` is entirely legal and renders
           `#shopify-section-group-header`, and matching Dawn's filename meant
           the walk did not stop there at all. Measured on a theme that scrolls
           an inner container: it went straight past and forced `overflow:
           visible !important` onto the scrollport itself, and the page could
           not be scrolled. Match the PREFIX, which is the part Shopify
           actually guarantees. */
        if (node.id && node.id.indexOf('shopify-section-group-') === 0) break;
        node = node.parentNode;
      }

      // 2. still buried: something paints above us. `z-index: 60` on our
      //    wrapper is a guess about the theme, so read what actually won and
      //    go one better rather than picking another magic number.
      var r = bar.getBoundingClientRect();
      var hit = document.elementFromPoint(Math.round(r.left + r.width / 2), Math.round(r.top + r.height / 2));
      var z = 0, n = hit;
      while (n && n.nodeType === 1 && n !== document.documentElement) {
        var v = parseInt(window.getComputedStyle(n).zIndex, 10);
        if (!isNaN(v) && v > z) z = v;
        n = n.parentNode;
      }
      if (!z) return;
      /* 2147483647 is the largest CSS <integer>. `z + 1` past it is invalid, so
         the whole declaration is dropped and the escape hatch silently does
         nothing — which is exactly the case it exists for, an occluder parked
         at the ceiling. Measured emitting `z-index: 2147483648 !important`. */
      var MAX_Z = 2147483647;
      if (z >= MAX_Z) return;
      var wrap = root.closest ? root.closest(wrapSel) : null;
      // `position` needs the same weight as the z-index it enables, or a theme's
      // own `position` on that wrapper leaves the z-index inert.
      forceDecls(wrap || root.parentNode || root,
        'position:relative !important;z-index:' + Math.min(z + 1, MAX_Z) + ' !important');
    }


    /* The scroll position has to come from whatever is actually scrolling.
       When a theme (or a smooth-scroll library, or a drawer that locks the
       page) puts the page inside an inner scroll container, pageYOffset stays
       0 for the whole session, `stuck` is never true and the bar never pins —
       with the setting on, the CSS correct and nothing to see in the settings. */
    var altScroller = null;
    function findScroller() {
      var node = root.parentNode;
      while (node && node.nodeType === 1 && node !== document.body) {
        if (isScroller(node)) return node;
        node = node.parentNode;
      }
      return null;
    }
    function scrollY() {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      if (y) return y;
      if (!altScroller) altScroller = findScroller();
      return altScroller ? altScroller.scrollTop : 0;
    }


    return {
      scrollY: scrollY,
      findScroller: findScroller,
      ensureVisible: ensureBarVisible,
      /* Call on every tick while stuck. `isHidden` is the section's own
         scroll-up state: it translates the head off screen legitimately, so
         the slip detector must not read that as a broken pin. */
      onStick: function (isHidden) {
        if (pinChecked && pinRepairs < 5 && !isHidden &&
            head && head.getBoundingClientRect().top < -1) {
          pinChecked = false;
          pinRepairs++;
        }
        ensurePinWorks();
      }
    };
  };
})();
